/**
 * Enquiry types for the two contact forms.
 *
 *  - TENANT_ENQUIRY_TYPES: a customer contacting a cleaning business through the
 *    tenant website's Contact page. Stored in `callback_requests.enquiry_type`
 *    (the SQL CHECK in 20261102022211_contact_enquiry_types.sql lists the same
 *    values — keep them in step) and shown in the owner's Enquiries inbox and
 *    owner notification email. Deliberately generic: not every business offers
 *    every type of clean, so the list never names services.
 *  - SALES_ENQUIRY_TYPES: a cleaning business owner contacting us from the sales
 *    site's Contact page. Goes into the email subject and body.
 *
 * Runtime-agnostic (no imports, no Deno.*) so the edge functions, the browser
 * (re-exported from src/lib/enquiry-types.ts) and the unit tests share one list.
 */

export const TENANT_ENQUIRY_TYPES = [
  { value: 'booking', label: 'Booking or availability' },
  { value: 'quote', label: 'Get a quote' },
  { value: 'existing_booking', label: 'About an existing booking' },
  { value: 'feedback', label: 'Feedback or a complaint' },
  { value: 'recruitment', label: 'Recruitment (work for us)' },
  { value: 'partner', label: 'Partner with us' },
  { value: 'other', label: 'Something else' },
] as const;

export type TenantEnquiryType = (typeof TENANT_ENQUIRY_TYPES)[number]['value'];

/**
 * Messages that are not sales leads: job applicants and would-be partners.
 * They stay in the inbox but are kept out of lead and conversion numbers.
 */
export const NON_LEAD_ENQUIRY_TYPES: readonly TenantEnquiryType[] = ['recruitment', 'partner'];

export const isTenantEnquiryType = (value: unknown): value is TenantEnquiryType =>
  TENANT_ENQUIRY_TYPES.some((t) => t.value === value);

/** Label for a stored type, or null when missing/unknown (older messages). */
export const tenantEnquiryTypeLabel = (value: unknown): string | null =>
  TENANT_ENQUIRY_TYPES.find((t) => t.value === value)?.label ?? null;

/** Short label for chips and email subjects ("Recruitment", not "Recruitment (work for us)"). */
export const tenantEnquiryTypeShortLabel = (value: unknown): string | null => {
  if (value === 'recruitment') return 'Recruitment';
  return tenantEnquiryTypeLabel(value);
};

/** True for job applications and partnership approaches. */
export const isNonLeadEnquiryType = (value: unknown): boolean =>
  typeof value === 'string' && (NON_LEAD_ENQUIRY_TYPES as readonly string[]).includes(value);

/** Whether a message should count as a sales lead (missing type = yes, as before). */
export const countsAsLead = (row: { enquiry_type?: string | null }): boolean =>
  !isNonLeadEnquiryType(row.enquiry_type);

export const SALES_ENQUIRY_TYPES = [
  { value: 'demo', label: 'Book a demo' },
  { value: 'pricing', label: 'Pricing and plans' },
  { value: 'setup', label: 'Getting set up' },
  { value: 'switching', label: 'Moving from another system' },
  { value: 'billing', label: 'Billing and my account' },
  { value: 'technical', label: 'Technical help' },
  { value: 'partner', label: 'Partner with us' },
  { value: 'careers', label: 'Recruitment / careers' },
  { value: 'press', label: 'Press and media' },
  { value: 'other', label: 'Something else' },
] as const;

export type SalesEnquiryType = (typeof SALES_ENQUIRY_TYPES)[number]['value'];

export const isSalesEnquiryType = (value: unknown): value is SalesEnquiryType =>
  SALES_ENQUIRY_TYPES.some((t) => t.value === value);

export const salesEnquiryTypeLabel = (value: unknown): string | null =>
  SALES_ENQUIRY_TYPES.find((t) => t.value === value)?.label ?? null;

/**
 * Owner notification copy for a Contact page message (send-owner-notification).
 * With a type: "[Partner with us] New message from Jo Bloggs". Job applications
 * and partnership approaches carry a note so they aren't mistaken for leads.
 * Without one (older clients / callback requests) it returns null and the
 * function keeps its generic wording.
 */
export function ownerContactCopy(
  type: unknown,
  customerName: string,
): { subject: string; typeLabel: string; note: string | null } | null {
  const typeLabel = tenantEnquiryTypeLabel(type);
  if (!typeLabel) return null;
  const who = customerName.trim() || 'a customer';
  const note =
    type === 'recruitment'
      ? "This is someone asking about working for you, not a sales lead. It won't count towards your conversion numbers."
      : type === 'partner'
        ? "This is a partnership approach, not a sales lead. It won't count towards your conversion numbers."
        : null;
  return { subject: `[${tenantEnquiryTypeShortLabel(type)}] New message from ${who}`, typeLabel, note };
}

/** "[Partner with us] New message from Jo Bloggs" */
export function salesEnquirySubject(type: SalesEnquiryType, name: string): string {
  const who = name.trim() || 'The Polished Version website';
  return `[${salesEnquiryTypeLabel(type)}] New message from ${who}`;
}
